import api from './api';

/**
 * 个人工作情报(work-intel)API
 * 后端一律以 JWT 的用户为准,前端无需也不能传 userId。
 */

export interface WorkIntelTodo {
  id: string;
  content: string;
  owner?: string | null;
  dueAt?: string | null;
  urgency: 'high' | 'medium' | 'low';
  status: 'open' | 'doing' | 'done' | 'ignored';
  batchId: string;
  /** 支撑该待办的原始消息 msgId 数组(溯源;历史数据为空=无法精确回溯) */
  sourceMsgIds?: string[] | null;
  /** 相关联系人:从 sourceMsgIds 反查的原始对话说话人(本人 isSelf=true 特别标记;contactId 可点开跳人物) */
  relatedContacts?: { name: string; isSelf: boolean; contactId?: string | null }[] | null;
  createdAt: string;
  /** 最后一次状态变更时间(已完成历史按此展示办结时刻) */
  updatedAt?: string;
}

/** 待办溯源:一条原始聊天消息 */
export interface WorkIntelTraceMsg {
  msgId: string;
  time?: string | null;
  talker: string;
  talkerName: string;
  isGroup: boolean;
  isSelf: boolean;
  sender: string;
  content: string;
}

export interface WorkIntelKnowledge {
  id: string;
  category: string;
  subCategory?: string | null;
  title: string;
  summary: string;
  vectorId?: string | null;
  occurredAt?: string | null;
  location?: string | null;
  createdAt: string;
}

export interface WorkIntelContact {
  id: string;
  contactWxid: string;
  contactName?: string | null;
  role?: string | null;
  personality?: string | null;
  /** 公司/单位(AI 从对话推断,历史数据可能为空) */
  company?: string | null;
  /** 职务 */
  position?: string | null;
  /** 级别:高层/中层/基层/未知 */
  seniority?: string | null;
  /** 手动指定的上级联系人 id(用户在图谱里下拉选定同单位人物);为空=交回 AI 自动推断 */
  managerContactId?: string | null;
  /** 该人出现过的其他称呼(wxid/微信号/群昵称),JSON 数组 */
  aliases?: string[] | null;
  /** 该联系人出现过的群聊名称(来源群),JSON 数组 */
  sourceGroups?: string[] | null;
  /** 研判出现次数(该人出现在多少个研判分片里),≠ 真实消息条数 */
  msgCount: number;
  /** 真实聊天消息条数(该人发过的消息数);性格画像展示门槛依据 */
  realMsgCount?: number;
  /** 汇报对象画像(领导/业主专用,按聊天记录 LLM 提炼) */
  audienceProfile?: AudienceProfile | null;
  /** 同事工作画像(所有联系人可用,手动"研判画像/重新研判"触发) */
  personaProfile?: ColleaguePersona | null;
  /** 领导标记(与"领导信息"tab 同判定,联系人卡片据此换色并展示汇报画像一句话) */
  isLeader?: boolean;
  lastInteractAt?: string | null;
}

/** 同事工作画像:和这个人共事该怎么配合(维度只在详情弹窗展示,卡片不铺开) */
export interface ColleaguePersona {
  oneLiner?: string;
  workStyle?: string;
  workTraits?: string;
  workCharacter?: string;
  commStyle?: string;
  advice?: string;
  basedOnMsgs?: number;
  generatedAt?: string;
}

/** 我的画像:用户本人的职场工作画像(基于本人全部发言,增量更新) */
export interface SelfPersona {
  oneLiner?: string;
  workStyle?: string;
  strengths?: string;
  workCharacter?: string;
  commStyle?: string;
  thinking?: string;
  growth?: string;
  basedOnMsgs?: number;
  rounds?: number;
  generatedAt?: string;
}

/** 汇报对象画像:给这个人写文档/PPT 时该怎么写 */
export interface AudienceProfile {
  oneLiner?: string;
  thinking?: string;
  focus?: string;
  bizTendency?: string;
  commStyle?: string;
  docPreference?: string;
  advice?: string;
  basedOnMsgs?: number;
  generatedAt?: string;
  vectorId?: string | null;
}

/**
 * 联系人性格画像展示门槛:真实聊天消息 < 此值时不展示性格,改显示"数据不足"
 * (消息太少时 AI 对性格的判断不可靠,不做臆测)。前端唯一真源,后端只提供 realMsgCount。
 */
export const PERSONA_MIN_MSGS = 20;

export interface WorkIntelRelation {
  id: string;
  fromWxid: string;
  toWxid: string;
  relationType?: string | null;
  weight: number;
  evidence?: string | null;
}

/** contacts/relations 列表接口附带的元信息:本人标识集合(用于图谱把本人端点合并到"我") */
export interface WorkIntelListMeta {
  selfIds?: string[];
}

export interface WorkIntelBatch {
  id: string;
  msgCount: number;
  triggerReason: string;
  modelUsed?: string | null;
  promptTokens: number;
  completionTokens: number;
  status: string;
  error?: string | null;
  startedAt: string;
  finishedAt?: string | null;
}

export const workIntelApi = {
  overview: async () => {
    const r = await api.get('/work-intel/overview');
    return r.data;
  },
  radar: async () => {
    const r = await api.get('/work-intel/radar');
    return r.data;
  },
  knowledge: async (page = 1, pageSize = 20, category?: string) => {
    const r = await api.get('/work-intel/knowledge', { params: { page, pageSize, category } });
    return r.data;
  },
  todos: async (page = 1, pageSize = 50, status?: string) => {
    const r = await api.get('/work-intel/todos', { params: { page, pageSize, status } });
    return r.data;
  },
  /** 待办溯源:该事项依据的原始聊天消息(legacy=true 为溯源上线前的历史数据,无标记) */
  todoTrace: async (id: string) => {
    const r = await api.get(`/work-intel/todos/${id}/trace`);
    return r.data;
  },
  updateTodoStatus: async (id: string, status: string) => {
    const r = await api.patch(`/work-intel/todos/${id}/status`, { status });
    return r.data;
  },
  contacts: async (page = 1, pageSize = 50) => {
    const r = await api.get('/work-intel/contacts', { params: { page, pageSize } });
    return r.data;
  },
  /** 手工修正联系人档案(名字/公司/职务/级别/上级)。手工值 AI 永不覆盖;传空串/null=清空并恢复交给 AI */
  updateContact: async (
    id: string,
    body: {
      contactName?: string;
      company?: string;
      position?: string;
      seniority?: string;
      managerContactId?: string | null;
    },
  ) => {
    const r = await api.patch(`/work-intel/contacts/${id}`, body);
    return r.data;
  },
  relations: async (page = 1, pageSize = 100) => {
    const r = await api.get('/work-intel/relations', { params: { page, pageSize } });
    return r.data;
  },
  /** 领导/业主清单(含汇报对象画像) */
  leaders: async () => {
    const r = await api.get('/work-intel/leaders');
    return r.data;
  },
  /** 生成/刷新某领导的汇报对象画像(LLM 提炼,入知识库) */
  genLeaderProfile: async (contactId: string) => {
    const r = await api.post(`/work-intel/leaders/${contactId}/profile`);
    return r.data;
  },
  /** 从领导信息移除(仅标记不再当领导,联系人保留) */
  /** 生成/重新研判某联系人的同事工作画像 */
  generatePersona: async (contactId: string) => {
    const r = await api.post(`/work-intel/contacts/${contactId}/persona`);
    return r.data;
  },
  /** 获取本人工作画像 */
  selfProfile: async () => {
    const r = await api.get('/work-intel/self-profile');
    return r.data as { ok: boolean; profile: SelfPersona | null; selfMsgCount: number };
  },
  /** 生成/重新研判本人工作画像(增量更新) */
  generateSelfProfile: async () => {
    const r = await api.post('/work-intel/self-profile');
    return r.data;
  },
  excludeLeader: async (contactId: string) => {
    const r = await api.delete(`/work-intel/leaders/${contactId}`);
    return r.data;
  },
  batches: async (page = 1, pageSize = 20) => {
    const r = await api.get('/work-intel/batches', { params: { page, pageSize } });
    return r.data;
  },
  retryBatch: async (batchId: string) => {
    const r = await api.post(`/work-intel/retry/${batchId}`);
    return r.data;
  },
  bufferStatus: async () => {
    const r = await api.get('/work-intel/buffer-status');
    return r.data;
  },
};
